import type { Config } from 'tailwindcss';
import materialColors from './styles/colors.json';

export default {
	content: [
		"./index.html",
		"./src/**/*.{js,ts,jsx,tsx,css}",
		'./src/pages/**/*.{js,ts,jsx,tsx,mdx}',
		'./src/components/**/*.{js,ts,jsx,tsx,mdx}',
		'./src/app/**/*.{js,ts,jsx,tsx,mdx}',
		'./src/modules/**/*.{js,ts,jsx,tsx,mdx}',
	],
	theme: {
		// Screens are defined explicitly (not via `extend`) and in ascending
		// min-width order. Adding a range screen like `tablet` disables
		// Tailwind's automatic min-width sorting of responsive variants, so the
		// emission order follows this config order. Keeping it ascending ensures
		// larger breakpoints (e.g. `xl`) still override smaller ones (e.g. `xs`).
		screens: {
			xs: '320px',
			sm: '640px',
			md: '768px',
			tablet: { min: '768px', max: '1023.98px' },
			lg: '1024px',
			xl: '1280px',
			'2xl': '1536px',
		},
		extend: {
			width: {
				maxScreenContent: '1440px',
			},
			fontFamily: {
				poppins: 'Poppins',
				robotomono: 'Roboto Mono',
			},
			borderWidth: {
				'3': '3px',
			},
			fontSize: {
				'display-large': [
					'57px',
					{
						lineHeight: '64px',
						letterSpacing: '-0.25px',
					},
				],
				'display-medium': [
					'45px',
					{
						lineHeight: '52px',
						letterSpacing: '0',
					},
				],
				'display-small': [
					'36px',
					{
						lineHeight: '44px',
						letterSpacing: '0',
					},
				],
				'headline-xx-large': [
					'40px',
					{
						lineHeight: '52px',
						letterSpacing: '0',
					},
				],
				'headline-xx-large2': [
					'40px',
					{
						lineHeight: '52px',
						letterSpacing: '0',
						fontWeight: '600',
					},
				],
				'headline-x-large': [
					'36px',
					{
						lineHeight: '44px',
						letterSpacing: '0',
						fontWeight: '600',
					},
				],
				'headline-large': [
					'32px',
					{
						lineHeight: '40px',
						letterSpacing: '0',
						fontWeight: '600',
					},
				],
				'headline-medium': [
					'28px',
					{
						lineHeight: '36px',
						letterSpacing: '0',
						fontWeight: '500',
					},
				],
				'headline-small': [
					'24px',
					{
						lineHeight: '32px',
						letterSpacing: '0',
						fontWeight: '400',
					},
				],
				'headline-x-small': [
					'20px',
					{
						lineHeight: '28px',
						letterSpacing: '0',
						fontWeight: '400',
					},
				],
				'title-large': [
					'22px',
					{
						lineHeight: '28px',
						letterSpacing: '0.1px',
					},
				],
				'title-large2': [
					'24px',
					{
						lineHeight: '32px',
						letterSpacing: '0',
						fontWeight: '600',
					},
				],
				'title-medium': [
					'16px',
					{
						lineHeight: '24px',
						letterSpacing: '0.15px',
					},
				],
				'title-medium2': [
					'20px',
					{
						lineHeight: '28px',
						letterSpacing: '0.15px',
					},
				],
				'title-small': [
					'14px',
					{
						lineHeight: '20px',
						letterSpacing: '0.1px',
					},
				],
				'title-small2': [
					'16px',
					{
						lineHeight: '24px',
						letterSpacing: '0.1px',
						fontWeight: '400',
					},
				],
				'body-large': [
					'18px',
					{
						lineHeight: '28px',
						letterSpacing: '0.5px',
						fontWeight: '400',
					},
				],
				'body-medium': [
					'16px',
					{
						lineHeight: '24px',
						letterSpacing: '0.25px',
						fontWeight: '400',
					},
				],
				'body-small': [
					'14px',
					{
						lineHeight: '20px',
						letterSpacing: '0',
						fontWeight: '400',
					},
				],
				'body-x-small': [
					'12px',
					{
						lineHeight: '16px',
						letterSpacing: '0.4px',
					},
				],
				'body-x-small2': [
					'12px',
					{
						lineHeight: '20px',
						letterSpacing: '0',
						fontWeight: '400',
					},
				],
				'body-xx-small': [
					'10px',
					{
						lineHeight: '16px',
						letterSpacing: '0px',
						fontWeight: '400',
					},
				],
				'label-large': [
					'18px',
					{
						lineHeight: '24px',
						letterSpacing: '0px',
						fontWeight: '400',
					},
				],
				'label-large2': [
					'18px',
					{
						lineHeight: '24px',
						letterSpacing: '0.1px',
						fontWeight: '500',
					},
				],
				'label-semibold': [
					'14px',
					{
						lineHeight: '16px',
						letterSpacing: '0px',
						fontWeight: '600',
					},
				],
				'label-medium': [
					'16px',
					{
						lineHeight: '24px',
						letterSpacing: '0.5px',
						fontWeight: '400',
					},
				],
				'label-small': [
					'14px',
					{
						lineHeight: '16px',
						letterSpacing: '0.4px',
					},
				],
				'label-small2': [
					'14px',
					{
						lineHeight: '16px',
						letterSpacing: '0',
						fontWeight: '400',
					},
				],
				'label-x-small': [
					'12px',
					{
						lineHeight: '16px',
						letterSpacing: '0',
						fontWeight: '400',
					},
				],
				'label-xx-small': [
					'10px',
					{
						lineHeight: '16px',
						letterSpacing: '0',
						fontWeight: '400',
					},
				],
				'caption': [
					'12px',
					{
						lineHeight: '16px',
						letterSpacing: '0',
						fontWeight: '500',
					},
				],
				'table-header': [
					'16px',
					{
						lineHeight: '24px',
						letterSpacing: '0px',
						fontWeight: '600',
					},
				],
			},
			colors: { ...materialColors },
		},
	},
	plugins: [],
} satisfies Config;
